import { combineReducers, configureStore, type UnknownAction } from '@reduxjs/toolkit';
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
  type PersistedState,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import mapSlice from './features/mapSlice';
import reportQuerySlice from './features/reports/reportsQuerySlice';
import { reportReviewWorkflowReducer } from './features/reports/reportReviewWorkflow/reportReviewWorkflowSlice';
import { createReportReviewWorkflowListenerMiddleware } from './features/reports/reportReviewWorkflow/reportReviewWorkflowListeners';
import { notificationsReducer } from './features/notifications/notificationSlice';
import userAccountQuerySlice from './features/userAccounts/usersQuerySlice';
import { reportsApi } from './features/reports/reportsApi';
import { userAccountsApi } from './features/userAccounts/usersApi';
import { retentionGovernanceApi } from './features/retentionGovernance/retentionGovernanceApi';
import { resetStaleReadCache } from './features/staleReadFallback/staleReadCache';
import { resetReportPresenceLossPolicy } from './features/reports/reportReadFamilyPolicy';
import { migrations, normalizePersistedRootState } from './state-migrations';
import type { AppDependencies } from './infrastructure/appDependencies';

const appReducer = combineReducers({
  map: mapSlice,
  reportQuery: reportQuerySlice,
  reportReviewWorkflow: reportReviewWorkflowReducer,
  notifications: notificationsReducer,
  userAccountQuery: userAccountQuerySlice,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [retentionGovernanceApi.reducerPath]: retentionGovernanceApi.reducer,
  [userAccountsApi.reducerPath]: userAccountsApi.reducer,
});

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: UnknownAction
): ReturnType<typeof appReducer> => {
  if (action.type === 'LOGOUT_RESET') {
    resetStaleReadCache();
    resetReportPresenceLossPolicy();
    state = undefined;
  }

  return appReducer(state, action);
};

const migratePersistedState = createMigrate(migrations, { debug: false });

const migrate = async (state: PersistedState, currentVersion: number): Promise<PersistedState> => {
  const migratedState = await migratePersistedState(state, currentVersion);
  return normalizePersistedRootState(migratedState);
};

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['reportQuery', 'userAccountQuery'],
  version: 2,
  migrate,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const createAppStore = (dependencies: AppDependencies) => {
  const reportReviewWorkflowListenerMiddleware = createReportReviewWorkflowListenerMiddleware({
    updateReportStatus: dependencies.reportsBackend.updateReportStatus,
    finalizeReportRejection: dependencies.reportsBackend.finalizeReportRejection,
    updateReportSpecies: dependencies.reportsBackend.updateReportSpecies,
    updateReportReviewMetadata: dependencies.reportsBackend.updateReportReviewMetadata,
    classifyReportFaceScreeningFalsePositive:
      dependencies.reportsBackend.classifyReportFaceScreeningFalsePositive,
    removeReportFaceScreeningMedia: dependencies.reportsBackend.removeReportFaceScreeningMedia,
  });

  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: dependencies,
        },
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        .prepend(reportReviewWorkflowListenerMiddleware.middleware)
        .concat(
          reportsApi.middleware,
          retentionGovernanceApi.middleware,
          userAccountsApi.middleware
        ),
  });
};

export const createAppPersistor = (store: AppStore) => persistStore(store);

export type AppStore = ReturnType<typeof createAppStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
